/**
 * ComparePlans Component
 *
 * Feature comparison table for pricing tiers.
 * Pixel-perfect UI/UX optimized for mobile and desktop.
 * Features animated plan icons with hover effects.
 *
 * Design decisions:
 * - Plan selector matches brand toggle styling (cream bg, pill shape)
 * - Mobile CTA is full-width with 48px min height for touch targets
 * - Mosans font for prices, SAANS for body text
 * - Category icons use brand pink (#ffdbed bg, #ffa3d1 icon)
 * - Compact spacing without excess padding
 * - Premium accordion animations with staggered children
 *
 * Props:
 * - compact: When true, uses smaller typography and collapsible sections (for modal)
 * - hidePrice: When true, hides price display (useful when shown alongside PricingCards)
 * - hideCTA: When true, hides CTA buttons (useful when CTA is handled by parent)
 */
"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useRef, type ReactNode } from "react";
import Image from "next/image";
import { T } from "gt-next";
import { motion, AnimatePresence } from "framer-motion";
import { PRICING_COMPARISON, PLAN_INFO, ComparisonFeature, PlanType } from "./comparisonData";
import { TrendingUpIcon, type TrendingUpIconHandle } from "@/components/ui/trending-up";
import { SparklesIcon, type SparklesIconHandle } from "@/components/ui/sparkles-icon";
import { RocketIcon, type RocketIconHandle } from "@/components/ui/rocket-icon";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { TouchTooltip } from "@/components/ui/tooltip";

/** Icon size for comparison table plan icons */
const COMPARE_ICON_SIZE = 24;
const COMPARE_ICON_SIZE_COMPACT = 20;

// ── i18n: Static lookup tables for translatable strings ──────────────────

/** Comparison category name translations */
const CATEGORY_NAME_T: Record<string, ReactNode> = {
  "Growth & Engagement": <T>Growth & Engagement</T>,
  "AI & Targeting": <T>AI & Targeting</T>,
  "Analytics & Support": <T>Analytics & Support</T>
};

/** Feature row names (keyed by the raw name in comparisonData; product names stay as they are) */
const FEATURE_NAME_T: Record<string, ReactNode> = {
  "Monthly Followers": <T>Monthly Followers</T>,
  "Real Engagement": <T>Real Engagement</T>,
  "24/7 Live Growth": <T>24/7 Live Growth</T>,
  "AI-Optimization": <T>AI-Optimization</T>,
  "Smart Auto Targeting": <T>Smart Auto Targeting</T>,
  "Location Filter": <T>Location Filter</T>,
  "Age & Gender Filters": <T>Age & Gender Filters</T>,
  "Language Targeting": <T>Language Targeting</T>,
  "AI Image Posts": <T>AI Image Posts</T>,
  "Real-Time Analytics": <T>Real-Time Analytics</T>,
  "Inspiration Library": <T>Inspiration Library</T>,
  "PDF Growth Reports": <T>PDF Growth Reports</T>,
  "Dedicated Account Manager": <T>Dedicated Account Manager</T>
};

/** Feature cell values that contain words */
const FEATURE_VALUE_T: Record<string, ReactNode> = {
  "<b>4,000+ guaranteed</b>": <T><b>4,000+ guaranteed</b></T>,
  "15/mo": <T>15/mo</T>,
  "30/mo": <T>30/mo</T>
};

/** Feature tooltips */
const FEATURE_TOOLTIP_T: Record<string, ReactNode> = {
  "Estimated new monthly followers with full refund if no net-positive growth after 30 days": <T>Estimated new monthly followers with full refund if no net-positive growth after 30 days</T>,
  "Real active engagement from real people, no bots": <T>Real active engagement from real people, no bots</T>,
  "Continuous growth automation even while you sleep": <T>Continuous growth automation even while you sleep</T>,
  "AI-powered growth acceleration with 2.75x more followers per month": <T>AI-powered growth acceleration with 2.75x more followers per month</T>,
  "AI data models for profile optimization": <T>AI data models for profile optimization</T>,
  "Advanced AI audience targeting": <T>Advanced AI audience targeting</T>,
  "Target by geographic location": <T>Target by geographic location</T>,
  "Filter followers by demographics": <T>Filter followers by demographics</T>,
  "Reach followers in their language": <T>Reach followers in their language</T>,
  "AI-generated content posts per month": <T>AI-generated content posts per month</T>,
  "Live dashboard for growth and engagement metrics": <T>Live dashboard for growth and engagement metrics</T>,
  "Real engagement signals delivered in the critical first 30 minutes after posting to trigger the algorithm and maximize your reach. 3x higher reach on average.": <T>Real engagement signals delivered in the critical first 30 minutes after posting to trigger the algorithm and maximize your reach. 3x higher reach on average.</T>,
  "Viral content templates and ideas": <T>Viral content templates and ideas</T>,
  "Downloadable analytics reports": <T>Downloadable analytics reports</T>,
  "Personal manager for your account": <T>Personal manager for your account</T>
};

/** Feature name cell: translated node when known, else the raw HTML (product names) */
function FeatureName(t0) {
  const $ = _c(3);
  const {
    name
  } = t0;
  const translated = FEATURE_NAME_T[name];
  let t1;
  if ($[0] !== name || $[1] !== translated) {
    t1 = translated ? <span>{translated}</span> : <span dangerouslySetInnerHTML={{
      __html: name
    }} />;
    $[0] = name;
    $[1] = translated;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

/** Feature value cell text: translated node when known, else the raw HTML */
function FeatureValue(t0) {
  const $ = _c(3);
  const {
    value
  } = t0;
  const translated = FEATURE_VALUE_T[value];
  let t1;
  if ($[0] !== translated || $[1] !== value) {
    t1 = translated ? <span className="font-semibold">{translated}</span> : <span className="font-semibold" dangerouslySetInnerHTML={{
      __html: value
    }} />;
    $[0] = translated;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

/** CTA text translations for plan info */
const CTA_TEXT_T: Record<string, ReactNode> = {
  "Start growing": <T>Start growing</T>
};

/** Animation variants for accordion content */
const accordionVariants = {
  collapsed: {
    height: 0,
    opacity: 0,
    transition: {
      height: {
        duration: 0.3,
        ease: [0.4, 0, 0.2, 1] as const
      },
      opacity: {
        duration: 0.2,
        ease: "easeOut" as const
      }
    }
  },
  expanded: {
    height: "auto",
    opacity: 1,
    transition: {
      height: {
        duration: 0.35,
        ease: [0.4, 0, 0.2, 1] as const
      },
      opacity: {
        duration: 0.25,
        delay: 0.1,
        ease: "easeOut" as const
      }
    }
  }
};

/** Stagger animation for feature rows */
const containerVariants = {
  collapsed: {
    transition: {
      staggerChildren: 0.02,
      staggerDirection: -1 as const
    }
  },
  expanded: {
    transition: {
      staggerChildren: 0.04,
      delayChildren: 0.1
    }
  }
};
const itemVariants = {
  collapsed: {
    opacity: 0,
    y: -8,
    transition: {
      duration: 0.15,
      ease: "easeIn" as const
    }
  },
  expanded: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.25,
      ease: [0.4, 0, 0.2, 1] as const
    }
  }
};
interface ComparePlansProps {
  /** Compact mode for modal usage - smaller text, collapsible sections */
  compact?: boolean;
  /** Hide price display (when shown alongside PricingCards) */
  hidePrice?: boolean;
  /** Hide CTA buttons (when CTA is handled by parent) */
  hideCTA?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Controlled active plan (optional) */
  activePlan?: PlanType;
  /** Callback when plan changes (optional) */
  onPlanChange?: (plan: PlanType) => void;
  /** 
   * Filter which plans to show (for upgrade modal)
   * When provided, only these plans will be displayed
   * Defaults to all plans: ["Go", "Grow", "Glory"]
   */
  visiblePlans?: PlanType[];
  /** Active billing period - controls which price is displayed */
  activePeriod?: "Monthly" | "Yearly";
}
export default function ComparePlans(t0) {
  const $ = _c(88);
  const {
    compact: t1,
    hidePrice: t2,
    hideCTA: t3,
    className: t4,
    activePlan: controlledActivePlan,
    onPlanChange,
    visiblePlans,
    activePeriod: t5
  } = t0;
  const compact = t1 === undefined ? false : t1;
  const hidePrice = t2 === undefined ? false : t2;
  const hideCTA = t3 === undefined ? false : t3;
  const className = t4 === undefined ? "" : t4;
  const activePeriod = t5 === undefined ? "Monthly" : t5;
  const [internalActivePlan, setInternalActivePlan] = useState("Grow");
  const activePlan = controlledActivePlan || internalActivePlan;
  let t6;
  if ($[0] !== onPlanChange) {
    t6 = plan => {
      setInternalActivePlan(plan);
      if (onPlanChange) {
        onPlanChange(plan);
      }
    };
    $[0] = onPlanChange;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const setActivePlan = t6;
  let t7;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      0: true,
      1: true,
      2: true
    };
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  const [expandedCategories, setExpandedCategories] = useState(t7);
  const goIconRef = useRef(null);
  const growIconRef = useRef(null);
  const gloryIconRef = useRef(null);
  const iconSize = compact ? COMPARE_ICON_SIZE_COMPACT : COMPARE_ICON_SIZE;
  let t8;
  if ($[3] !== iconSize) {
    t8 = {
      Go: {
        icon: <TrendingUpIcon ref={goIconRef} size={iconSize} />,
        ref: goIconRef
      },
      Grow: {
        icon: <SparklesIcon ref={growIconRef} size={iconSize} />,
        ref: growIconRef
      },
      Glory: {
        icon: <RocketIcon ref={gloryIconRef} size={iconSize} autoAnimateInterval={5500} />,
        ref: gloryIconRef
      }
    };
    $[3] = iconSize;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  const planIcons = t8;
  let t9;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      Go: "#60a5fa",
      Grow: "#ffa3d1",
      Glory: "#c084fc"
    };
    $[5] = t9;
  } else {
    t9 = $[5];
  }
  const planIconColor = t9;
  let plans;
  let t10;
  if ($[6] !== visiblePlans) {
    plans = visiblePlans || ["Go", "Grow", "Glory"];
    t10 = plans.map(_temp).join(" ");
    $[6] = visiblePlans;
    $[7] = plans;
    $[8] = t10;
  } else {
    plans = $[7];
    t10 = $[8];
  }
  const gridTemplateColumns = `1.2fr ${t10}`;
  let t11;
  if ($[9] !== compact) {
    t11 = index => {
      if (compact) {
        setExpandedCategories(prev => ({
          ...prev,
          [index]: !prev[index]
        }));
      }
    };
    $[9] = compact;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  const toggleCategory = t11;
  const containerPadding = compact ? "px-0 py-2" : "px-4 pt-8 pb-16 md:px-6 md:pt-12 md:pb-24";
  const titleSize = compact ? "text-base" : "text-lg";
  const priceSize = compact ? "text-[36px]" : "text-[48px]";
  compact ? "text-[14px]" : "text-[15px]";
  const categoryIconSize = compact ? 36 : 48;
  compact ? "py-2.5 px-2" : "py-3.5 px-3";
  const t12 = `bg-white ${className}`;
  const t13 = `mx-auto max-w-[1136px] ${containerPadding}`;
  let t14;
  if ($[11] !== activePlan || $[12] !== plans || $[13] !== setActivePlan) {
    let t15;
    if ($[15] !== activePlan || $[16] !== setActivePlan) {
      t15 = plan_0 => {
        const isActive = activePlan === plan_0;
        const planInfo = PLAN_INFO[plan_0];
        const isFeatured = planInfo.featured;
        return <button key={plan_0} onClick={() => setActivePlan(plan_0)} className={`relative flex-1 rounded-lg px-3 py-3 text-[15px] font-semibold transition-[color,background-color,box-shadow] duration-200 ${isActive ? "bg-white text-black shadow-sm ring-1 ring-black/5" : "text-black/60 hover:bg-black/5 hover:text-black"}`}>{plan_0}{isFeatured && <span className={`absolute -right-1 -top-1 flex h-2.5 w-2.5 items-center justify-center rounded-full bg-pink ring-2 ring-white ${!isActive && "opacity-50"}`}><span className="h-1 w-1 rounded-full bg-black/50" /></span>}</button>;
      };
      $[15] = activePlan;
      $[16] = setActivePlan;
      $[17] = t15;
    } else {
      t15 = $[17];
    }
    t14 = plans.map(t15);
    $[11] = activePlan;
    $[12] = plans;
    $[13] = setActivePlan;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  let t15;
  if ($[18] !== t14) {
    t15 = <div className="mb-6 lg:hidden"><div className="flex rounded-xl bg-[#f3f3f1] p-1 ring-1 ring-black/5">{t14}</div></div>;
    $[18] = t14;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== activePeriod || $[21] !== activePlan || $[22] !== compact || $[23] !== hideCTA || $[24] !== hidePrice || $[25] !== planIcons) {
    t16 = !compact && !hidePrice && <div className="mb-6 lg:hidden"><div className="flex flex-col items-center gap-3 border-b py-4" style={{
        borderColor: "rgba(20, 20, 20, 0.08)"
      }}>{(() => {
          const planInfo_0 = PLAN_INFO[activePlan];
          const {
            icon
          } = planIcons[activePlan];
          const displayPrice = activePeriod === "Yearly" ? planInfo_0.yearlyPrice : planInfo_0.monthlyPrice;
          return <><div className="flex items-center gap-2.5 text-lg font-semibold" style={{
              color: "#141414"
            }}><span className="translate-y-[1px]" style={{
                color: planIconColor[activePlan]
              }}>{icon}</span>{planInfo_0.name}</div><div className="flex items-center gap-2"><span className="font-mosans text-[48px] leading-none" style={{
                color: "#141414"
              }}>${displayPrice}</span><div className="flex flex-col"><span className="text-base font-semibold" style={{
                  color: "#141414"
                }}><T>/mo</T></span><span className="text-[14px]" style={{
                  color: "rgba(20, 20, 20, 0.5)"
                }}>{activePeriod === "Yearly" ? <T>billed yearly</T> : <T>billed monthly</T>}</span></div></div>{!hideCTA && <a href={planInfo_0.ctaLink} className="btn-inset-auto w-full rounded-xl px-6 py-3.5 text-center text-base font-semibold transition-[opacity,transform,scale] duration-200 hover:opacity-90 active:scale-[0.98]" style={{
              minHeight: "48px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: planInfo_0.buttonBg,
              color: planInfo_0.buttonColor
            }}>{CTA_TEXT_T[planInfo_0.ctaText] || planInfo_0.ctaText}</a>}</>;
        })()}</div></div>;
    $[20] = activePeriod;
    $[21] = activePlan;
    $[22] = compact;
    $[23] = hideCTA;
    $[24] = hidePrice;
    $[25] = planIcons;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  const t17 = compact ? "bg-white" : "sticky z-20 bg-white";
  const t18 = compact ? undefined : "120px";
  const t19 = compact ? "16px" : "32px";
  const t20 = compact ? "12px" : "20px";
  let t21;
  if ($[27] !== gridTemplateColumns || $[28] !== t18 || $[29] !== t19 || $[30] !== t20) {
    t21 = {
      top: t18,
      display: "grid",
      gridTemplateColumns,
      paddingTop: t19,
      paddingBottom: t20,
      borderBottom: "1px solid rgba(20, 20, 20, 0.08)"
    };
    $[27] = gridTemplateColumns;
    $[28] = t18;
    $[29] = t19;
    $[30] = t20;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  const t22 = compact ? "20px" : "32px";
  let t23;
  if ($[32] !== t22) {
    t23 = {
      fontSize: t22,
      fontWeight: 600,
      color: "#141414"
    };
    $[32] = t22;
    $[33] = t23;
  } else {
    t23 = $[33];
  }
  let t24;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <T>Compare plans</T>;
    $[34] = t24;
  } else {
    t24 = $[34];
  }
  let t25;
  if ($[35] !== t23) {
    t25 = <div style={t23}>{t24}</div>;
    $[35] = t23;
    $[36] = t25;
  } else {
    t25 = $[36];
  }
  let t26;
  if ($[37] !== activePeriod || $[38] !== compact || $[39] !== hideCTA || $[40] !== hidePrice || $[41] !== planIcons || $[42] !== plans || $[43] !== priceSize || $[44] !== titleSize) {
    let t27;
    if ($[46] !== activePeriod || $[47] !== compact || $[48] !== hideCTA || $[49] !== hidePrice || $[50] !== planIcons || $[51] !== priceSize || $[52] !== titleSize) {
      t27 = plan_1 => {
        const planInfo_1 = PLAN_INFO[plan_1];
        const {
          icon: icon_0,
          ref
        } = planIcons[plan_1];
        const displayPrice_0 = activePeriod === "Yearly" ? planInfo_1.yearlyPrice : planInfo_1.monthlyPrice;
        return <div key={plan_1} className="flex flex-col items-start gap-2" onMouseEnter={() => ref.current?.startAnimation()} onMouseLeave={() => ref.current?.stopAnimation()}><div className={`flex items-center gap-2.5 ${titleSize} font-semibold`} style={{
            color: "#141414"
          }}><span className="flex translate-y-px" style={{
              color: planIconColor[plan_1]
            }}>{icon_0}</span>{planInfo_1.name}</div>{!hidePrice && <div className="flex items-end gap-1"><span className={`font-mosans ${priceSize} leading-none`} style={{
              color: "#141414"
            }}>${displayPrice_0}</span><div className="flex flex-col pb-0.5 pl-0.5"><span className={`${compact ? "text-sm" : "text-base"} font-semibold`} style={{
                color: "#141414"
              }}><T>/mo</T></span><span className="text-[14px]" style={{
                color: "rgba(20, 20, 20, 0.5)"
              }}>{activePeriod === "Yearly" ? <T>billed yearly</T> : <T>billed monthly</T>}</span></div></div>}{!hideCTA && <a href={planInfo_1.ctaLink} className={`btn-inset-auto ${compact ? "mt-1" : "mt-2"} rounded-xl ${compact ? "px-4 py-2 text-xs" : "px-5 py-2.5 text-sm"} font-semibold transition-[opacity,transform,scale] duration-200 hover:opacity-90 active:scale-[0.98]`} style={{
            backgroundColor: planInfo_1.buttonBg,
            color: planInfo_1.buttonColor
          }}>{CTA_TEXT_T[planInfo_1.ctaText] || planInfo_1.ctaText}</a>}</div>;
      };
      $[46] = activePeriod;
      $[47] = compact;
      $[48] = hideCTA;
      $[49] = hidePrice;
      $[50] = planIcons;
      $[51] = priceSize;
      $[52] = titleSize;
      $[53] = t27;
    } else {
      t27 = $[53];
    }
    t26 = plans.map(t27);
    $[37] = activePeriod;
    $[38] = compact;
    $[39] = hideCTA;
    $[40] = hidePrice;
    $[41] = planIcons;
    $[42] = plans;
    $[43] = priceSize;
    $[44] = titleSize;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  let t27;
  if ($[54] !== t17 || $[55] !== t21 || $[56] !== t25 || $[57] !== t26) {
    t27 = <div className={t17} style={t21}>{t25}{t26}</div>;
    $[54] = t17;
    $[55] = t21;
    $[56] = t25;
    $[57] = t26;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== categoryIconSize || $[60] !== compact || $[61] !== expandedCategories || $[62] !== gridTemplateColumns || $[63] !== plans || $[64] !== toggleCategory) {
    t28 = PRICING_COMPARISON.map((category, categoryIndex) => <div key={categoryIndex}><div className={`${compact ? "" : "z-5 sticky"} bg-white ${compact ? "cursor-pointer select-none" : ""}`} style={{
        display: "grid",
        gridTemplateColumns,
        top: compact ? undefined : "312px",
        paddingTop: categoryIndex === 0 ? compact ? "16px" : "24px" : compact ? "12px" : "16px",
        paddingBottom: compact ? "8px" : "12px",
        marginTop: categoryIndex === 0 ? "0" : compact ? "8px" : "16px"
      }} onClick={() => toggleCategory(categoryIndex)}><div className="flex items-center gap-3"><Image src={category.icon} alt={category.name} width={categoryIconSize} height={categoryIconSize} /><span className={`${compact ? "text-base" : "text-lg"} font-semibold`} style={{
            color: "#141414"
          }}>{CATEGORY_NAME_T[category.name] || category.name}</span>{compact && <motion.div animate={{
            rotate: expandedCategories[categoryIndex] ? 180 : 0
          }} transition={{
            duration: 0.3,
            ease: [0.4, 0, 0.2, 1] as const
          }}><ChevronDownIcon className="h-4 w-4" style={{
              color: "rgba(20, 20, 20, 0.5)"
            }} /></motion.div>}</div></div>{compact ? <AnimatePresence initial={false}>{expandedCategories[categoryIndex] && <motion.div key={`desktop-${categoryIndex}`} initial="collapsed" animate="expanded" exit="collapsed" variants={accordionVariants} style={{
          overflow: "hidden"
        }}><motion.div variants={containerVariants}>{category.features.map((feature, featureIndex) => <motion.div key={featureIndex} variants={itemVariants}><FeatureRowDesktop feature={feature} compact={compact} visiblePlans={plans} gridTemplateColumns={gridTemplateColumns} /></motion.div>)}</motion.div></motion.div>}</AnimatePresence> : category.features.map((feature_0, featureIndex_0) => <FeatureRowDesktop key={featureIndex_0} feature={feature_0} compact={compact} visiblePlans={plans} gridTemplateColumns={gridTemplateColumns} />)}</div>);
    $[59] = categoryIconSize;
    $[60] = compact;
    $[61] = expandedCategories;
    $[62] = gridTemplateColumns;
    $[63] = plans;
    $[64] = toggleCategory;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  if ($[66] !== t27 || $[67] !== t28) {
    t29 = <div className="hidden lg:block">{t27}{t28}</div>;
    $[66] = t27;
    $[67] = t28;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  let t30;
  if ($[69] !== activePlan || $[70] !== categoryIconSize || $[71] !== compact || $[72] !== expandedCategories || $[73] !== toggleCategory) {
    t30 = PRICING_COMPARISON.map((category_0, categoryIndex_0) => <div key={categoryIndex_0}><div className={`${compact ? "" : "sticky z-10"} flex items-center gap-3 bg-white ${compact ? "cursor-pointer select-none pb-2 pt-4" : "pb-3 pt-6"}`} style={{
        top: compact ? undefined : "132px",
        borderTop: categoryIndex_0 > 0 ? "1px solid rgba(20, 20, 20, 0.08)" : "none",
        marginTop: categoryIndex_0 > 0 ? compact ? "4px" : "8px" : "0"
      }} onClick={() => toggleCategory(categoryIndex_0)}><Image src={category_0.icon} alt={category_0.name} width={categoryIconSize} height={categoryIconSize} /><span className={`${compact ? "text-base" : "text-lg"} flex-1 font-semibold`} style={{
          color: "#141414"
        }}>{CATEGORY_NAME_T[category_0.name] || category_0.name}</span>{compact && <motion.div animate={{
          rotate: expandedCategories[categoryIndex_0] ? 180 : 0
        }} transition={{
          duration: 0.3,
          ease: [0.4, 0, 0.2, 1] as const
        }}><ChevronDownIcon className="h-4 w-4" style={{
            color: "rgba(20, 20, 20, 0.5)"
          }} /></motion.div>}</div>{compact ? <AnimatePresence initial={false}>{expandedCategories[categoryIndex_0] && <motion.div key={`mobile-${categoryIndex_0}`} initial="collapsed" animate="expanded" exit="collapsed" variants={accordionVariants} style={{
          overflow: "hidden"
        }}><motion.div variants={containerVariants}>{category_0.features.map((feature_1, featureIndex_1) => <motion.div key={featureIndex_1} variants={itemVariants}><FeatureRowMobile feature={feature_1} activePlan={activePlan} compact={compact} /></motion.div>)}</motion.div></motion.div>}</AnimatePresence> : category_0.features.map((feature_2, featureIndex_2) => <FeatureRowMobile key={featureIndex_2} feature={feature_2} activePlan={activePlan} compact={compact} />)}</div>);
    $[69] = activePlan;
    $[70] = categoryIconSize;
    $[71] = compact;
    $[72] = expandedCategories;
    $[73] = toggleCategory;
    $[74] = t30;
  } else {
    t30 = $[74];
  }
  let t31;
  if ($[75] !== t30) {
    t31 = <div className="lg:hidden">{t30}</div>;
    $[75] = t30;
    $[76] = t31;
  } else {
    t31 = $[76];
  }
  let t32;
  if ($[77] !== t29 || $[78] !== t31) {
    t32 = <div>{t29}{t31}</div>;
    $[77] = t29;
    $[78] = t31;
    $[79] = t32;
  } else {
    t32 = $[79];
  }
  let t33;
  if ($[80] !== t13 || $[81] !== t15 || $[82] !== t16 || $[83] !== t32) {
    t33 = <div className={t13}>{t15}{t16}{t32}</div>;
    $[80] = t13;
    $[81] = t15;
    $[82] = t16;
    $[83] = t32;
    $[84] = t33;
  } else {
    t33 = $[84];
  }
  let t34;
  if ($[85] !== t12 || $[86] !== t33) {
    t34 = <section className={t12}>{t33}</section>;
    $[85] = t12;
    $[86] = t33;
    $[87] = t34;
  } else {
    t34 = $[87];
  }
  return t34;
}
function _temp() {
  return "1fr";
}
interface FeatureRowDesktopProps {
  feature: ComparisonFeature;
  compact?: boolean;
  /** Which plans to show cells for */
  visiblePlans: PlanType[];
  /** Dynamic grid template columns */
  gridTemplateColumns: string;
}
function FeatureRowDesktop(t0) {
  const $ = _c(19);
  const {
    feature,
    compact: t1,
    visiblePlans,
    gridTemplateColumns
  } = t0;
  t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== gridTemplateColumns) {
    t2 = {
      gridTemplateColumns,
      borderTop: "1px solid rgba(20, 20, 20, 0.08)",
      padding: "14px 12px"
    };
    $[0] = gridTemplateColumns;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      color: "#141414"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== feature.name) {
    t4 = <FeatureName name={feature.name} />;
    $[3] = feature.name;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== feature.tooltip) {
    t5 = feature.tooltip && <TouchTooltip content={<p className="text-[13px] leading-[1.45] text-gray-600">{FEATURE_TOOLTIP_T[feature.tooltip] ?? feature.tooltip}</p>} side="top" variant="dark" contentClassName="max-w-[240px] rounded-xl px-4 py-3"><button type="button" className="inline-flex items-center" aria-label="More info"><Image src="/img/pricing/ic_tooltip.avif" alt="" width={16} height={16} className="cursor-pointer opacity-40 transition-opacity hover:opacity-60" /></button></TouchTooltip>;
    $[5] = feature.tooltip;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = <div className="flex items-center gap-1.5 text-[15px] font-medium" style={t3}>{t4}{t5}</div>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== feature || $[11] !== visiblePlans) {
    let t8;
    if ($[13] !== feature) {
      t8 = plan => <FeatureCell key={plan} value={feature[plan]} />;
      $[13] = feature;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    t7 = visiblePlans.map(t8);
    $[10] = feature;
    $[11] = visiblePlans;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[15] !== t2 || $[16] !== t6 || $[17] !== t7) {
    t8 = <div className="group -mx-3 grid cursor-default rounded-lg transition-[background-color] duration-200 ease-out hover:bg-cream" style={t2}>{t6}{t7}</div>;
    $[15] = t2;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}
interface FeatureRowMobileProps {
  feature: ComparisonFeature;
  activePlan: PlanType;
  compact?: boolean;
}
function FeatureRowMobile(t0) {
  const $ = _c(14);
  const {
    feature,
    activePlan,
    compact: t1
  } = t0;
  t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      borderTop: "1px solid rgba(20, 20, 20, 0.08)"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      color: "#141414"
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== feature.name) {
    t4 = <FeatureName name={feature.name} />;
    $[2] = feature.name;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== feature.tooltip) {
    t5 = feature.tooltip && <TouchTooltip content={<p className="text-[13px] leading-[1.45] text-gray-600">{FEATURE_TOOLTIP_T[feature.tooltip] ?? feature.tooltip}</p>} side="top" variant="dark" contentClassName="max-w-[220px] rounded-xl px-4 py-3"><button type="button" className="inline-flex items-center" aria-label="More info"><Image src="/img/pricing/ic_tooltip.avif" alt="" width={16} height={16} className="cursor-pointer opacity-40" /></button></TouchTooltip>;
    $[4] = feature.tooltip;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t4 || $[7] !== t5) {
    t6 = <div className="flex items-center gap-1.5 text-[15px] font-medium" style={t3}>{t4}{t5}</div>;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = feature[activePlan];
  let t8;
  if ($[9] !== t7) {
    t8 = <MobileFeatureCell value={t7} />;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t6 || $[12] !== t8) {
    t9 = <div className="flex items-center justify-between py-3.5" style={t2}>{t6}{t8}</div>;
    $[11] = t6;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  return t9;
}
interface FeatureCellProps {
  value: boolean | string;
}
function FeatureCell(t0) {
  const $ = _c(3);
  const {
    value
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      color: "#141414"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== value) {
    t2 = <div className="flex items-center text-[15px] font-medium" style={t1}>{value === true ? <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="9" cy="9" r="9" fill="#141414" /><path d="M5 9L7.5 11.5L13 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg> : value === false ? <span style={{
        color: "rgba(20, 20, 20, 0.3)"
      }}>—</span> : <FeatureValue value={value} />}</div>;
    $[1] = value;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function MobileFeatureCell(t0) {
  const $ = _c(3);
  const {
    value
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      color: "#141414"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== value) {
    t2 = <div className="text-[15px] font-medium" style={t1}>{value === true ? <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="9" cy="9" r="9" fill="#141414" /><path d="M5 9L7.5 11.5L13 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg> : value === false ? <span style={{
        color: "rgba(20, 20, 20, 0.3)"
      }}>—</span> : <FeatureValue value={value} />}</div>;
    $[1] = value;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}